import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import FileUpload from "components/common/FileUpload";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import { ErrorMessage, FieldArray, Form, Formik } from "formik";
import { createMeetingRoomValues } from "components/constants/InitialFormValues";
import CustomLabel from "components/common/CustomLabel";
import CustomField from "components/common/CustomField";
import SingleSelect from "components/common/SingleSelect";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { format } from "date-fns";
import {
  allWeekDays,
  defaultOpeningClosingTime,
} from "components/constants/Constants";
import { CircleX } from "lucide-react";
import MultipleSelect from "components/common/MultipleSelect";
import FormButtons from "components/common/FormButtons";
import { addMeetingRoomSchema } from "components/common/Validation";
import Spinners from "components/common/Spinners";
import { getCityList } from "store/actions/citySlice";
import { getAmenityList } from "store/actions/amenitySlice";
import {
  addWorkspace,
  getWorkspaceDetails,
  updateWorkspace,
} from "store/actions/workspaceSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getBranchListByCity } from "store/actions/branchSlice";
import { showAlert } from "store/reducers/alertReducer";
import {
  parse,
  differenceInMinutes,
  isBefore,
  isEqual,
  isAfter,
} from "date-fns";
import CustomTimeRangePicker from "components/common/CustomTimeRangePicker";
import CustomTimeRangePicker2 from "components/common/CustomTimeRangePicker2";
import { setPageTitle } from "components/common/Utils";

const AddMeetingRoom = ({ mode }) => {
  const { t } = useTranslation();
  document.title = mode === "add" ? t("Add Meeting Room | HubHouse") : t("Edit Meeting Room | HubHouse");


  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const {
    isLoading,
    loading,
    workspaceDetails: meetingDetails,
  } = useSelector((state) => state.Workspace);

  const [formValues, setFormValues] = useState(createMeetingRoomValues);

  const [openingTimes, setOpeningTimes] = useState(defaultOpeningClosingTime);

  const { citiesList } = useSelector((state) => state.Cities);
  const { branchesByCity } = useSelector((state) => state.Branches);
  const { amenitiesList } = useSelector((state) => state.Amenities);

  useEffect(() => {
    // GET CITY LIST
    const fetchCityList = () => {
      dispatch(getCityList());
    };
    // GET AMENITIES LIST
    const fetchAmenitiesList = () => {
      dispatch(getAmenityList());
    };

    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getWorkspaceDetails(payload));
    };

    fetchCityList();
    fetchAmenitiesList();
    mode === "edit" && getDetails();
  }, [mode]);

  const fetchBranchesByCity = (cityId) => {
    const payload = {
      cityId,
    };
    dispatch(getBranchListByCity(payload));
  };

  // SET THE VALUES
  useEffect(() => {
    if (mode === "edit" && !loading && meetingDetails) {
      // Destructure keys from meetingDetails
      const {
        name,
        nameAr,
        description,
        descriptionAr,
        imageUrls,
        branchId,
        cityId,
        amenities,
        meetingRoomDetails,
        customRates,
        openingHours,
      } = meetingDetails || {};

      cityId?._id && fetchBranchesByCity(cityId?._id);

      const updatedAmenities = amenities?.map((item) => item?._id) || [];

      const updatedCustomPrice =
        customRates?.map((item) => ({
          days: item?.dayName,
          startTime: item?.startTime,
          endTime: item?.endTime,
          price: item?.rate,
        })) || [];

      setFormValues((prevValues) => ({
        ...prevValues,
        name,
        nameAr,
        description,
        descriptionAr,
        imageUrls,
        cityId: cityId?._id,
        branchId: branchId?._id,
        amenities: updatedAmenities,
        price: meetingRoomDetails?.price,
        numberOfSeats: meetingRoomDetails?.numberOfSeats,
        customPrice: updatedCustomPrice,
      }));
      // SET OPENING AND CLOSING TIMES
      const updatedOpeningHours =
        openingHours?.map((item) => ({
          id: item?._id,
          dayOfWeek: item?.dayOfWeek,
          allowed24Hour: item?.allowed24Hour,
          openingTime: item?.openingTime,
          closingTime: item?.closingTime,
          closed: item?.closed,
        })) || [];
      setOpeningTimes(updatedOpeningHours);
    }
  }, [mode, meetingDetails]);

  const handleToggle = (id, key, value) => {
    setOpeningTimes((prev) =>
      prev.map((day) =>
        day.id === id
          ? {
            ...day,
            [key]: value,
            ...(key === "closed" && value ? { allowed24Hour: false } : {}),
          }
          : day
      )
    );
  };

  const handleTimeChange = (id, type, value) => {
    // const formattedTime = format(value, "hh:mm a");
    setOpeningTimes((prevTimes) =>
      prevTimes.map((item) =>
        item.id === id ? { ...item, [type]: value } : item
      )
    );
  };

  const handleOnSubmit = (values) => {
    const buttonId = document.activeElement.id;
    // UPDATE STATUS ACCORDING TO ACTION TYPE
    const updatedStatus =
      buttonId === "draftBtn" ? 3 : buttonId === "submitBtn" ? 1 : 1;

    const {
      spaceTypeInt,
      spaceType,
      name,
      nameAr,
      description,
      descriptionAr,
      imageUrls,
      branchId,
      cityId,
      price,
      numberOfSeats,
      amenities,
      customPrice,
    } = values;

    const errors = validateCustomPrices(customPrice);

    if (errors.length > 0) {
      dispatch(showAlert({ text: errors.join("\n"), type: "error" }));
      return;
    }

    let data = new FormData();
    data.append("spaceTypeInt", "2"); // STATIC FOR MEETING ROOM
    data.append("spaceType", "meetingRoom"); // STATIC FOR MEETING ROOM
    data.append("name", name);
    data.append("nameAr", nameAr);
    data.append("description", description);
    data.append("descriptionAr", descriptionAr);

    const filesToUpload = imageUrls?.map((fileObj) => fileObj);
    filesToUpload.forEach((file) => {
      data.append("imageUrls", file);
    });

    data.append("cityId", cityId);
    data.append("branchId", branchId);

    amenities.forEach((amenity, index) => {
      data.append(`amenities[${index}]`, amenity);
    });
    openingTimes?.forEach((item, index) => {
      data.append(`openingHours[${index}][dayOfWeek]`, item?.dayOfWeek);
      data.append(`openingHours[${index}][openingTime]`, item.openingTime);
      data.append(`openingHours[${index}][closingTime]`, item.closingTime);
      data.append(`openingHours[${index}][closed]`, false);
      data.append(
        `openingHours[${index}][allowed24Hour]`,
        item?.allowed24Hour?.toString()
      );
    });

    data.append("meetingRoomDetails[numberOfSeats]", numberOfSeats);
    data.append("meetingRoomDetails[price]", price);
    // data.append("meetingRoomDetails[discount]", "10");
    customPrice?.forEach((item, index) => {
      data.append(`customRates[${index}][dayName]`, item.days);
      data.append(`customRates[${index}][startTime]`, item.startTime);
      data.append(`customRates[${index}][endTime]`, item.endTime);
      data.append(`customRates[${index}][rate]`, item.price);
    });
    data.append("status", updatedStatus);

    if (mode === "add") {
      dispatch(addWorkspace(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => { });
    } else if (mode === "edit") {
      data.append("id", id);
      dispatch(updateWorkspace(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => { });
    }
  };

  const validateCustomPrices = (customPrices) => {
    const errors = [];
    const today = new Date();

    customPrices.forEach((entry, index) => {
      const { startTime, endTime, days } = entry;
      const start = parse(startTime, "hh:mm a", today);
      const end = parse(endTime, "hh:mm a", today);

      // Rule 1: Start must be before end
      if (!isBefore(start, end)) {
        errors.push(
          `Slot ${index + 1
          } (${days}): Start time must be earlier than end time.`
        );
        return;
      }

      // Rule 2: Start and end must not be equal
      if (isEqual(start, end)) {
        errors.push(
          `Slot ${index + 1
          } (${days}): Start time and end time cannot be the same.`
        );
        return;
      }

      // Rule 3: Duration must be in full hours
      const diff = differenceInMinutes(end, start);
      if (diff % 60 !== 0) {
        errors.push(
          `Slot ${index + 1
          } (${days}): Duration must be in full hours (e.g., 1hr, 2hr).`
        );
        return;
      }

      // Rule 4: End must not be 12:00 AM
      if (endTime === "12:00 AM") {
        errors.push(
          `Slot ${index + 1} (${days}): End time cannot be 12:00 AM.`
        );
        return;
      }

      // Rule 5: No overlapping time slots on the same day
      for (let j = 0; j < customPrices.length; j++) {
        if (j === index) continue;

        const compare = customPrices[j];
        if (compare.days !== days) continue;

        const compareStart = parse(compare.startTime, "hh:mm a", today);
        const compareEnd = parse(compare.endTime, "hh:mm a", today);

        const isOverlap =
          (isBefore(start, compareEnd) && isAfter(end, compareStart)) ||
          isEqual(start, compareStart);

        if (isOverlap) {
          errors.push(
            `Slot ${index + 1} (${days}): Time overlaps with another slot.`
          );
          break;
        }
      }
    });

    return errors;
  };
  return (
    <React.Fragment>
      <div className="page-content common_page">
        {loading && mode === "edit" ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle
                title={
                  mode === "add"
                    ? t("Add New Meeting Room")
                    : t("Edit Meeting Room")
                }
              />
            </Row>
            <Formik
              initialValues={formValues}
              validationSchema={addMeetingRoomSchema}
              enableReinitialize={true}
              onSubmit={handleOnSubmit}
            >
              {({ values, setFieldValue, errors, touched }) => (
                <Form>
                  <Row className="common_wrapper">
                    <Col lg={12}>
                      <FileUpload
                        uploadedFiles={values.imageUrls || []}
                        setUploadedFiles={(files) =>
                          setFieldValue("imageUrls", files)
                        }
                        maxFiles={5}
                        maxFileSize={0.6 * 1024 * 1024}
                        acceptedFileTypes={[
                          "image/png",
                          "image/jpeg",
                          "image/svg+xml",
                        ]}
                        requireSquare={false}
                        requireRectangle={false}
                        className={
                          touched?.imageUrls && errors.imageUrls
                            ? "invalid"
                            : ""
                        }
                      />
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="name"
                          label={t("English Title")}
                        />
                        <CustomField
                          name="name"
                          type="text"
                          placeholder={t("Enter English Title")}
                          touched={touched}
                          errors={errors}
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="nameAr"
                          label={t("Arabic Title")}
                        />
                        <CustomField
                          name="nameAr"
                          type="text"
                          placeholder={t("Enter Arabic Title")}
                          touched={touched}
                          errors={errors}
                          isArabic={true}
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="description"
                          label={t("English Description")}
                        />
                        <CustomField
                          name="description"
                          placeholder="Enter English Description"
                          touched={touched}
                          errors={errors}
                          as="textarea"
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="form_field">
                        <CustomLabel
                          htmlFor="descriptionAr"
                          label={t("Arabic Description")}
                        />
                        <CustomField
                          name="descriptionAr"
                          placeholder="Enter Arabic Description"
                          touched={touched}
                          errors={errors}
                          as="textarea"
                          isArabic={true}
                        />
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="bg_white br_14 p_23_30">
                        <h2 className="fs_20 mb-4">{t("Location")}</h2>
                        <Row>
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel htmlFor="cityId" label={t("City")} />
                              <SingleSelect
                                options={citiesList?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                value={values.cityId}
                                placeholder={t("Select City")}
                                handleSingleSelectChange={(e) => {
                                  fetchBranchesByCity(e.value);
                                  setFieldValue("cityId", e.value);
                                  setFieldValue("branchId", null);
                                }}
                                className={`${errors.cityId && touched.cityId
                                    ? "invalid"
                                    : ""
                                  }`}
                                error={errors?.cityId}
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="branchId"
                                label={t("Branch")}
                              />
                              <SingleSelect
                                options={branchesByCity?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                value={values.branchId}
                                placeholder={t("Select Branch")}
                                handleSingleSelectChange={(e) => {
                                  setFieldValue("branchId", e.value);
                                }}
                                className={`${errors.branchId && touched.branchId
                                    ? "invalid"
                                    : ""
                                  }`}
                                error={errors?.branchId}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                    <Col lg={12} style={{ marginTop: 21 }}>
                      <div className="bg_white br_14 p_23_30">
                        <h2 className="fs_20 mb-4">{t("Opening Time")}</h2>
                        {openingTimes?.length === 0 ? (
                          <p>{t("No data found")}</p>
                        ) : (
                          openingTimes?.map((item) => (
                            <div className="d-flex mb-2" key={item.id}>
                              <div className="w-50 d-flex">
                                <div className="left_section d-flex align-items-center me-5">
                                  <p className="fs_14" style={{ minWidth: 40 }}>
                                    {item.dayOfWeek?.slice(0, 3)}
                                  </p>
                                  <div className="d-flex align-items-center gap-3">
                                    <InputSwitch
                                      checked={item.allowed24Hour}
                                      onChange={(e) =>
                                        handleToggle(
                                          item.id,
                                          "allowed24Hour",
                                          e.value
                                        )
                                      }
                                      disabled={item.closed} // Disable when "Closed" is true
                                    />
                                    <span>{t("Allow 24h Open")}</span>
                                  </div>
                                </div>
                                {/* <div className="middle_section d-flex align-items-center">
                                  <InputSwitch
                                    checked={item.closed}
                                    onChange={(e) =>
                                      handleToggle(item.id, "closed", e.value)
                                    }
                                  />
                                  <span className="mx-3">{t("Closed")}</span>
                                </div> */}
                              </div>

                              <div
                                className="right_section g_end w-50"
                                style={{ minHeight: 50 }}
                              >
                                {item.closed ? (
                                  <p className="fs_14 text-center w-100">
                                    {t("Closed")}
                                  </p>
                                ) : (
                                  <>
                                    <CustomTimeRangePicker2
                                      startTime={item.openingTime || "12:00 AM"}
                                      endTime={item.closingTime || "12:00 PM"}
                                      onStartChange={(val) => {
                                        handleTimeChange(
                                          item.id,
                                          "openingTime",
                                          val
                                        );
                                      }}
                                      onEndChange={(val) =>
                                        handleTimeChange(
                                          item.id,
                                          "closingTime",
                                          val
                                        )
                                      }
                                      className="big_time_range_picker"
                                      disabled={item.allowed24Hour}
                                    />

                                    {/* <h2 className="fs_14 mx-4">to</h2>
                                    <Calendar
                                      value={
                                        new Date(
                                          `1970-01-01 ${item.closingTime}`
                                        )
                                      }
                                      hourFormat="12"
                                      className="custom_time_picker"
                                      onChange={(e) =>
                                        handleTimeChange(
                                          item.id,
                                          "closingTime",
                                          e.value
                                        )
                                      }
                                      timeOnly
                                      disabled={item.allowed24Hour} // Disable if 24h Open is true
                                    /> */}
                                  </>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </Col>
                    <Col lg={12} style={{ marginTop: 21 }}>
                      <div className="bg_white br_14 p_23_30">
                        <h2 className="fs_20 mb-4">
                          {t("Meeting Room Details")}
                        </h2>
                        <Row>
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="price"
                                label={t("Base Price")}
                              />
                              <CustomField
                                name="price"
                                type="text"
                                placeholder={t("Enter Base Price")}
                                touched={touched}
                                errors={errors}
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="numberOfSeats"
                                label={t("Number Of Seat")}
                              />
                              <CustomField
                                name="numberOfSeats"
                                type="text"
                                placeholder={t("Enter Number Of Seat")}
                                touched={touched}
                                errors={errors}
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                    <Col lg={12} style={{ marginTop: 21 }}>
                      <div className="bg_white br_14 p_23_30">
                        <h2 className="fs_20 mb-4">{t("Custom Price")}</h2>
                        <FieldArray
                          name="customPrice"
                          render={(arrayHelpers) => (
                            <>
                              {values?.customPrice?.map((item, index) => (
                                <Row key={index} className="custom_price_inner">
                                  <Col lg={8}>
                                    <div className="mb-3 form_field">
                                      <CustomLabel
                                        htmlFor={`customPrice[${index}].days`}
                                        label={t("Day")}
                                      />
                                      <SingleSelect
                                        options={allWeekDays}
                                        value={item.days}
                                        placeholder={t("Select Day")}
                                        // handleMultipleSelectChange={(e) => {
                                        //   console.log(e);
                                        //   setFieldValue(
                                        //     `customPrice[${index}].days`,
                                        //     e
                                        //   );
                                        // }}
                                        handleSingleSelectChange={(e) => {
                                          setFieldValue(
                                            `customPrice[${index}].days`,
                                            e.value
                                          );
                                        }}
                                        className={`${touched?.customPrice?.[index]?.days &&
                                            errors?.customPrice?.[index]?.days
                                            ? "invalid"
                                            : ""
                                          }`}
                                      />
                                      <ErrorMessage
                                        name={`customPrice[${index}].days`}
                                        component="div"
                                        className="text-danger"
                                      />
                                    </div>
                                  </Col>
                                  <Col lg={4}>
                                    <div className=" d-flex justify-content-end align-items-start">
                                      {/* {values?.customPrice?.length > 1 && ( */}
                                      <button
                                        type="button"
                                        className="transparent_btn text_danger"
                                        onClick={() =>
                                          arrayHelpers.remove(index)
                                        }
                                      >
                                        <CircleX />
                                      </button>
                                      {/* )} */}
                                    </div>
                                  </Col>
                                  <Col lg={8}>
                                    <Row>
                                      <div className="d-flex align-item-center gap-3">
                                        <div className="form_field">
                                          <CustomLabel
                                            htmlFor={`customPrice[${index}].startTime`}
                                            label="Time Range"
                                          />
                                          <CustomTimeRangePicker
                                            startTime={
                                              item.startTime || "12:00 AM"
                                            }
                                            endTime={item.endTime || "12:00 PM"}
                                            onStartChange={(val) =>
                                              setFieldValue(
                                                `customPrice[${index}].startTime`,
                                                val
                                              )
                                            }
                                            onEndChange={(val) =>
                                              setFieldValue(
                                                `customPrice[${index}].endTime`,
                                                val
                                              )
                                            }
                                          />
                                          {/* <Calendar
                                            value={
                                              item.startTime
                                                ? new Date(
                                                    `1970-01-01 ${item.startTime}`
                                                  )
                                                : null
                                            }
                                            hourFormat="12"
                                            className="custom_time_picker w-100"
                                            timeOnly
                                            onChange={(e) => {
                                              const formattedTime = format(
                                                e.value,
                                                "hh:mm a"
                                              ); // Format to AM/PM
                                              setFieldValue(
                                                `customPrice[${index}].startTime`,
                                                formattedTime
                                              );
                                            }}
                                          /> */}
                                        </div>

                                        <div
                                          className="form_field"
                                          style={{ minWidth: 100 }}
                                        >
                                          <CustomLabel
                                            htmlFor={`customPrice[${index}].price`}
                                            label="Price"
                                          />
                                          <CustomField
                                            name={`customPrice[${index}].price`}
                                            type="text"
                                            placeholder="Enter Price"
                                            touched={touched}
                                            errors={errors}
                                          />
                                        </div>
                                      </div>
                                    </Row>
                                  </Col>
                                </Row>
                              ))}
                              <Col lg={12}>
                                <div className="g_end">
                                  <button
                                    type="button"
                                    className="main_btn mt-4"
                                    onClick={() =>
                                      arrayHelpers.push({
                                        days: [],
                                        startTime: "12:30 AM", // Set default time as string with AM/PM
                                        endTime: "01:30 AM",
                                        price: "",
                                      })
                                    }
                                  >
                                    {t("Add Custom Price")}
                                  </button>
                                </div>
                              </Col>
                            </>
                          )}
                        />
                      </div>
                    </Col>
                    <Col lg={12} style={{ marginTop: 21 }}>
                      <div className="bg_white br_14 p_23_30">
                        <h2 className="fs_20 mb-4">{t("Amenities")}</h2>
                        <Row>
                          <Col lg={6}>
                            <div className="mb-3 form_field">
                              <CustomLabel
                                htmlFor={`amenities`}
                                label={t("Select Amenities")}
                              />
                              <MultipleSelect
                                options={amenitiesList?.map((item) => ({
                                  name: item?.name,
                                  value: item?._id,
                                }))}
                                value={values.amenities}
                                placeholder={t("Select Amenities")}
                                handleMultipleSelectChange={(e) => {
                                  setFieldValue(`amenities`, e);
                                }}
                                className={`${touched.amenities && errors.amenities
                                    ? "invalid"
                                    : ""
                                  }`}
                              />
                              <ErrorMessage
                                name={`amenities`}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                  <Row className="px-0 my-4">
                    <FormButtons
                      isDraftBtn={true}
                      isLoading={isLoading}
                      addBtnText={mode === "add" ? t("Add New") : t("Update")}
                    />
                  </Row>
                </Form>
              )}
            </Formik>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddMeetingRoom;
